import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery } from 'react-query'

import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { useMutation, getErrorMessage, resolve } from '@hapana/shared/web/services/gqty'

import { getSitesPaginated } from '../services/sites.service'
import { buildLocationSwitchPath } from '../utils/location-switch-path.util'
import useSiteStore from '../stores/site.store'
import { usePermissions } from '../stores/permissions.store'
import { useMe } from './me.hooks'
import { useFeatureFlag, FeatureFlagKeys } from './useFeatureFlag'

const PAGE_SIZE = 50

const SORT_VALUES = {
  A_TO_Z: 'az',
  Z_TO_A: 'za',
} as const

const DEFAULT_TAB_ROUTES: Record<string, string> = {
  HOME: '',
  SCHEDULE: 'schedule',
  CLIENTS: 'clients',
  RETAIL: 'retail',
  REPORTS: 'reports',
}

interface UseLocationSelectorOptions {
  /** Called after a site is selected and navigation completes */
  onSiteSelected?: () => void
  /** Redirect path after site selection (overrides default tab) */
  redirectPath?: string | null
}

export interface SiteRow {
  siteId: string
  name: string
  settings?: {
    customization?: {
      colors?: Array<{ id: string; color: string }>
    }
  }
}

interface SitesPaginatedPage {
  sites?: {
    results?: Array<{ cursor: string; row: SiteRow }>
    pageInfo?: {
      hasNextPage: boolean
      endCursor: string | null
      totalRecords: number
    }
  }
}

export function useLocationSelector(options: UseLocationSelectorOptions = {}) {
  const { onSiteSelected, redirectPath } = options
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { openToast } = useToast()
  const { value: keepPageOnSwitchFlag } = useFeatureFlag(FeatureFlagKeys.KeepPageOnLocationSwitch, {
    defaultValue: false,
  })
  const shouldKeepPageOnSwitch = keepPageOnSwitchFlag === true

  const { setCurrentSite, currentSite } = useSiteStore()
  const {
    getAccessibleSites,
    hasUniversalSiteAccess,
    isLoading: isPermissionsLoading,
    parsedPermissions,
  } = usePermissions()
  const { data: userMe, refetch: refetchMe, isLoading: isLoadingMe } = useMe()

  const accessibleSiteIds = useMemo(() => getAccessibleSites(), [parsedPermissions])
  const hasAllSitesAccess = useMemo(() => hasUniversalSiteAccess(), [parsedPermissions])

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState<string>(SORT_VALUES.A_TO_Z)

  // Debounce search input (300ms)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const handleSetSearch = useCallback((value: string) => {
    setSearch(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300)
  }, [])

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const userFavorites: string[] = userMe?.me?.favoriteSiteIds || []

  // Toggle favorite mutation (still GQty — no paginated equivalent needed)
  const [toggleFavoriteMutation, { isLoading: isTogglingFavorite }] = useMutation(
    (mutation, args: { siteId: string }) => {
      return mutation.toggleSiteFavorite({ siteId: args.siteId })
    },
    {
      onComplete: async (result: boolean) => {
        await refetchMe()
        openToast({
          title: result
            ? t('core:locationSwitch.toast.favoriteAdded')
            : t('core:locationSwitch.toast.favoriteRemoved'),
          severity: 'success',
        })
      },
      onError: (error: Error) => {
        openToast({
          title: t('core:locationSwitch.toast.favoriteError'),
          description: getErrorMessage(error, t('core:locationSwitch.toast.favoriteError')),
          severity: 'error',
        })
      },
    }
  )

  // Sites query — React Query infinite scroll with cursor pagination
  const normalizedSearch = debouncedSearch?.toLowerCase() || undefined
  const sortDirection = sort === SORT_VALUES.A_TO_Z ? 'ASC' : 'DESC'

  const queryParams = {
    search: normalizedSearch,
    pageSize: PAGE_SIZE,
    orderBy: [{ column: 'name', direction: sortDirection }],
    filter: { status: 'active', isV2Enabled: true },
  }

  const {
    data: sitesData,
    isLoading: isSitesLoading,
    isFetching: isSitesFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery([getSitesPaginated.key, queryParams], getSitesPaginated.query, {
    getNextPageParam: (lastPage: SitesPaginatedPage) => {
      if (lastPage?.sites?.pageInfo?.hasNextPage) {
        return { cursor: lastPage.sites.pageInfo.endCursor }
      }
      return undefined
    },
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,
  })

  // Flatten paginated results
  const allSites: SiteRow[] = useMemo(() => {
    if (!sitesData?.pages) {
      return []
    }
    return sitesData.pages.flatMap(
      (page: SitesPaginatedPage) =>
        page?.sites?.results?.map((r) => r.row).filter((row): row is SiteRow => Boolean(row)) ?? []
    )
  }, [sitesData])

  const totalCount: number =
    (sitesData?.pages?.[0] as SitesPaginatedPage | undefined)?.sites?.pageInfo?.totalRecords ?? 0

  const isLoading = isSitesLoading || isPermissionsLoading

  // Filter sites by user permissions
  const sites: SiteRow[] = useMemo(() => {
    if (hasAllSitesAccess) {
      return allSites
    }
    if (accessibleSiteIds.length === 0) {
      return []
    }
    return allSites.filter((site) => site.siteId && accessibleSiteIds.includes(site.siteId))
  }, [allSites, accessibleSiteIds, hasAllSitesAccess])

  const handleToggleFavorite = useCallback(
    (siteId: string) => {
      if (isTogglingFavorite) {
        return
      }
      toggleFavoriteMutation({ args: { siteId } })
    },
    [toggleFavoriteMutation, isTogglingFavorite]
  )

  const isFavorite = useCallback(
    (siteId: string): boolean => userFavorites.includes(siteId),
    [userFavorites]
  )

  const handleSiteSelect = useCallback(
    async (site: SiteRow & Parameters<typeof setCurrentSite>[0]) => {
      setCurrentSite(site)

      if (redirectPath) {
        const cleanPath = redirectPath.startsWith('/') ? redirectPath.slice(1) : redirectPath
        navigate(`/s/${site.siteId}/${cleanPath}`)
        onSiteSelected?.()
        return
      }

      const carriedPath = shouldKeepPageOnSwitch
        ? buildLocationSwitchPath(pathname, site.siteId)
        : null
      if (carriedPath) {
        navigate(carriedPath)
        onSiteSelected?.()
        return
      }

      try {
        const defaultTab = await resolve(
          ({ query }) => {
            const s = query.site({ siteId: site.siteId })
            return s?.settings?.location?.operations?.systemPreferences?.defaultBusinessProfileTab
          },
          { cachePolicy: 'cache-first' }
        )

        const route = defaultTab ? DEFAULT_TAB_ROUTES[defaultTab] : undefined
        navigate(`/s/${site.siteId}/${route || ''}`)
      } catch {
        navigate(`/s/${site.siteId}`)
      }

      onSiteSelected?.()
    },
    [setCurrentSite, navigate, redirectPath, onSiteSelected, shouldKeepPageOnSwitch, pathname]
  )

  const favoriteLocations = useMemo(
    () => sites.filter((loc) => loc.siteId && loc.name && userFavorites.includes(loc.siteId)),
    [sites, userFavorites]
  )

  const nonFavoriteLocations = useMemo(
    () => sites.filter((loc) => loc.siteId && loc.name && !userFavorites.includes(loc.siteId)),
    [sites, userFavorites]
  )

  const hasLoaded =
    !isLoading && !isLoadingMe && !isPermissionsLoading && !isSitesFetching && !error

  return {
    // Data
    sites,
    favoriteLocations,
    nonFavoriteLocations,
    currentSite,
    userFavorites,

    // State
    search,
    sort,
    isLoading: isLoading || isLoadingMe,
    isTogglingFavorite,
    error,
    hasLoaded,
    isEmpty: hasLoaded && sites.length === 0 && !search,

    // Infinite scroll
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    totalCount,
    loadedCount: allSites.length,

    // Actions
    setSearch: handleSetSearch,
    setSort,
    handleToggleFavorite,
    handleSiteSelect,
    isFavorite,

    // Constants
    SORT_VALUES,
  }
}
